/**
 * CoreLangContext
 * ───────────────
 * Provides the bilingual text object `T` to every component.
 * `coreMode` is fetched from the platform settings API once on app load.
 *
 *   coreMode = 'global'  →  T = STRINGS.global  (English, LTR)
 *   coreMode = 'persian' →  T = STRINGS.persian  (فارسی, RTL)
 *
 * Usage:
 *   import { useCoreT } from '../context/CoreLangContext';
 *   const T = useCoreT();
 *   <h1>{T.overview}</h1>
 */

import React, { createContext, useContext, useEffect, useMemo } from 'react';
import STRINGS from '../lang/strings';
import { useUnifiedUi } from '../lang/uiStore';

const CoreLangContext = createContext(STRINGS.global);

// RTL languages — used to pick text direction for custom-language dashboards.
const RTL_LANGS = new Set(['fa', 'ar', 'he', 'ur']);
const VAZIR_FONT = "'Vazirmatn', 'DM Sans', sans-serif";

/** Inject Vazirmatn font (used for Persian/Arabic/Hebrew dashboards) */
function injectVazirmatn() {
  if (document.getElementById('vazirmatn-font')) return;
  const link = document.createElement('link');
  link.id   = 'vazirmatn-font';
  link.rel  = 'stylesheet';
  link.href = '/fonts/fonts.css';
  document.head.appendChild(link);
}

/**
 * Build the active T object.
 *  - coreLang 'en'/'' + legacy coreMode 'persian'  → built-in STRINGS.persian
 *  - coreLang === 'fa'                              → built-in STRINGS.persian
 *  - any other coreLang with a translation map      → overlay map on STRINGS.global
 *  - otherwise                                      → STRINGS.global (English)
 */
function buildT(coreMode, coreLang, coreTranslations) {
  const lang = (coreLang || '').toLowerCase();

  if (!lang) {
    return coreMode === 'persian' ? STRINGS.persian : STRINGS.global;
  }
  if (lang === 'en') return STRINGS.global;
  if (lang === 'fa') return STRINGS.persian;            // hand-crafted Persian catalogue

  // Custom language: overlay translated keys onto the English base so every
  // T.<key> the dashboard reads resolves (untranslated keys keep English).
  const map = (coreTranslations && coreTranslations[lang]) || {};
  const rtl = RTL_LANGS.has(lang);
  return {
    ...STRINGS.global,
    ...map,
    dir: rtl ? 'rtl' : 'ltr',
    lang,
    fontFamily: rtl ? VAZIR_FONT : STRINGS.global.fontFamily,
    titleFont:  rtl ? VAZIR_FONT : STRINGS.global.titleFont,
  };
}

export function CoreLangProvider({ children, coreMode, coreLang = 'fa', coreTranslations = {} }) {
  const base = useMemo(
    () => buildT(coreMode, coreLang, coreTranslations),
    [coreMode, coreLang, coreTranslations]
  );
  // Overlay the owner-managed unified store (from /api/i18n) — one source shared
  // with the SSR pages and the other dashboards. Meta (dir/lang/fonts) is kept.
  const unified = useUnifiedUi(base.lang);
  const T = useMemo(() => ({
    ...base, ...unified,
    lang: base.lang, dir: base.dir, fontFamily: base.fontFamily, titleFont: base.titleFont,
  }), [base, unified]);

  // Apply dir + lang to <html>
  useEffect(() => {
    document.documentElement.setAttribute('dir',  T.dir);
    document.documentElement.setAttribute('lang', T.lang);
    document.documentElement.style.setProperty('--core-font', T.fontFamily);
    if (T.dir === 'rtl') injectVazirmatn();
  }, [T]);

  return (
    <CoreLangContext.Provider value={T}>
      {children}
    </CoreLangContext.Provider>
  );
}

/** Hook — returns the active T object */
export function useCoreT() {
  return useContext(CoreLangContext);
}

/** Convenience: returns current mode string */
export function useCoreMode() {
  const T = useContext(CoreLangContext);
  return T.lang === 'fa' ? 'persian' : 'global';
}

export default CoreLangContext;
